/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Card,
  CardMedia,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  Button,
  Chip,
} from "@mui/material";
import { Task as TaskIcon, Work as WorkIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { isLoggedIn } from "../session";
import { auth, db, getToken } from "../firebase";
import {
  collection,
  getDocs,
  where,
  query,
  doc,
  getDoc,
} from "firebase/firestore";
import BottomNav from "./BottomNav";

import logoSvg from "../imgs/logo_border.png";

const STATUS_LABELS = {
  pending: { label: "Pendiente", color: "warning" },
  accepted: { label: "Aceptada", color: "success" },
  rejected: { label: "Rechazada", color: "error" },
};

const Proposals = () => {
  const [uid, setUid] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState("");
  const [authUserId, setAuthUserId] = useState(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [userType, setUserType] = useState(null);
  const [clickedProposals, setClickedProposals] = useState({});
  const [categories, setCategories] = useState([
    "Diseño Web",
    "Diseño UX/UI",
    "Diseño Grafico",
    "Programación",
    "Desarrollo de Apps",
    "Community Manager",
    "Marketing Digital",
    "Diseño Multimedia",
    "Edición de Videos",
    "Edición de Imágenes",
    "Redacción de Contenidos",
    "Traducción",
  ]);

  useEffect(() => {
    const fetchAuthUserId = async () => {
      const user = await isLoggedIn();
      if (user) {
        setAuthUserId(user.uid);
      } else {
        navigate("/login"); // Redirect to login if not authenticated
      }
    };
    fetchAuthUserId();
  }, [navigate]);

  const handleButtonClick = (projectId, proposalId, freelancerUid) => {
    if (clickedProposals[proposalId]) {
      // If already clicked, navigate to chats
      navigate("/chats");
    } else {
      // If not clicked, mark as clicked and navigate to chatbox
      setClickedProposals((prev) => ({
        ...prev,
        [proposalId]: true,
      }));
      navigate(
        `/chatbox/${projectId}/${proposalId}/${freelancerUid}/${authUserId}`
      );
    }
  };

  const formatDate = (timeToDo) => {
    if (timeToDo instanceof Date) {
      return timeToDo.toLocaleDateString();
    } else if (timeToDo && timeToDo.toDate instanceof Function) {
      return timeToDo.toDate().toLocaleDateString();
    }
    return "";
  };

  useEffect(() => {
    const checkAuthAndFetchProposals = async () => {
      if (!isLoggedIn()) {
        navigate("/login");
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          console.error("No se pudo obtener el token de autenticación.");
          return;
        }
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No hay un usuario autenticado.");
          return;
        }
        const { uid } = currentUser;
        setUid(uid);
        setAuthUserId(uid); // Set authUserId to the current user's uid
        setLoading(true);

        const projectsSnapshot = await getDocs(collection(db, "projects"));
        const projectsData = projectsSnapshot.docs.map((doc) => ({
          id: doc.id,
          title: doc.data().title,
          skills: doc.data().skills,
          timeToDo: doc.data().timeToDo,
          uid: doc.data().uid,
        }));

        const allProposals = [];
        for (const project of projectsData) {
          const proposalsSnapshot = await getDocs(
            collection(db, "projects", project.id, "proposals")
          );
          const projectProposals = proposalsSnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id,
              projectId: project.id,
              projectTitle: project.title,
              projectSkills: project.skills,
              projectUid: project.uid,
              timeToDo: project.timeToDo ? project.timeToDo.toDate() : null,
              sentAt: data.timestamp ? data.timestamp.toDate() : null,
              status: data.status || "pending",
              uid: data.uid,
            };
          });
          allProposals.push(...projectProposals);
        }

        const userDocRef = doc(db, "users", uid);
        const userDocSnapshot = await getDoc(userDocRef);
        if (userDocSnapshot.exists()) {
          const userData = userDocSnapshot.data();
          setUserType(userData.userType); // Set the userType state
        } else {
          console.error("User document not found.");
        }

        setProposals(allProposals);
        setLoading(false);
      } catch (error) {
        console.error("Error al obtener propuestas:", error);
        setLoading(false);
      }
    };
    checkAuthAndFetchProposals();
  }, [navigate]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSkills(e.target.value);
  };

  const filteredProposals = proposals.filter((proposal) => {
    const proposalSkills = proposal.projectSkills;
    const skillsMatch = skills
      ? Array.isArray(proposalSkills)
        ? proposalSkills.includes(skills)
        : proposalSkills === skills
      : true;

    return (
      proposal.projectTitle &&
      proposal.projectTitle.toLowerCase().includes(searchTerm.toLowerCase()) &&
      skillsMatch
    );
  });

  const isProposalUnread = async (proposalId, projectId) => {
    try {
      const unreadQuery = query(
        collection(
          db,
          "projects",
          projectId,
          "proposals",
          proposalId,
          "messages"
        ),
        where("read", "==", false),
        where("senderId", "!=", authUserId)
      );
      const unreadSnapshot = await getDocs(unreadQuery);
      return unreadSnapshot.docs.length > 0;
    } catch (error) {
      console.error("Error checking unread messages:", error);
      return false;
    }
  };

  const styles = {
    img: {
      maxWidth: "150px",
      maxHeight: "40px",
      display: "block",
      margin: "20px auto",
    },
    slogan: {
      color: "text.secondary",
      fontSize: "14px",
      fontWeight: "bold",
    },
    iconContainer: {
      display: "flex",
      width: "100%",
      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
      marginTop: "20px",
      justifyContent: "space-around",
      color: "text.secondary",
    },
    buscar: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      width: "100%",
    },
    title: {
      fontSize: "24px",
      color: "primary.main",
      marginBottom: "7px",
    },
    description: {
      fontSize: "18px",
      marginBottom: "7px",
    },
    timeToDo: {
      color: "success.main",
      fontSize: "15px",
      marginBottom: "7px",
      fontFamily: (theme) => theme.custom.fontMono,
    },
    skills: {
      marginBottom: "7px",
    },
    price: {
      color: "success.main",
      marginBottom: "7px",
      fontFamily: (theme) => theme.custom.fontMono,
    },
    created: {
      fontSize: "10px",
      marginBottom: "7px",
      color: "text.secondary",
    },
    media: {
      width: "190px",
      marginTop: "25px",
    },
  };

  return (
    <Container>
      <AppBar position="absolute">
        <Toolbar sx={{ flexDirection: "column", alignItems: "center" }}>
          <CardMedia
            component="img"
            image={logoSvg}
            title="Skach"
            style={styles.media}
          />
          <Box>
            <Typography variant="h6" sx={styles.slogan}>
              ¡Crea un proyecto o busca uno!
            </Typography>
          </Box>
          <Toolbar sx={styles.iconContainer}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <IconButton href="/proyectos" sx={{ color: "text.secondary" }}>
                <WorkIcon />
              </IconButton>
              <Typography variant="caption" fontSize={9} align="center">
                Mis Proyectos
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <IconButton href="/propuestas" sx={{ color: "text.secondary" }}>
                <TaskIcon />
              </IconButton>
              <Typography variant="caption" fontSize={9} align="center">
                Mis Propuestas
              </Typography>
            </Box>
          </Toolbar>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 26, mb: 15 }}>
        <Box sx={styles.buscar}>
          <TextField
            label="Buscar por nombre de proyecto..."
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            sx={{ width: "70%" }}
          />
          <TextField
            select
            label="Habilidad"
            value={skills}
            onChange={handleCategoryChange}
            variant="outlined"
            sx={{ width: "28%", ml: 1 }}
          >
            <MenuItem value={null}>Habilidad</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {loading ? (
          <Box sx={{ textAlign: "center", color: "primary.main", marginTop: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {filteredProposals.length > 0 ? (
              <List>
                {filteredProposals.map((proposal) => (
                  <ListItem key={proposal.id} sx={{ display: "block", px: 0 }}>
                    <Card sx={{ p: 3, mb: 2.5, width: "100%" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Typography variant="h6" sx={styles.title}>
                          {proposal.projectTitle}
                        </Typography>
                        <Chip
                          size="small"
                          label={STATUS_LABELS[proposal.status]?.label || "Pendiente"}
                          color={STATUS_LABELS[proposal.status]?.color || "warning"}
                        />
                      </Box>

                      <Typography variant="body2" sx={styles.timeToDo}>
                        Propuesta enviada el:{" "}
                        {proposal.sentAt ? formatDate(proposal.sentAt) : "N/A"}
                      </Typography>
                      <Typography variant="body2" color="primary.main">
                        Descripción de la propuesta:
                      </Typography>
                      <Typography variant="body1" sx={styles.description}>
                        {proposal.description}
                      </Typography>
                      <Typography variant="body2" sx={styles.price}>
                        Presupuesto: USD$
                        <Box component="span" sx={styles.price}>{proposal.price}</Box>
                      </Typography>
                      <Typography variant="body2" sx={styles.skills}>
                        Habilidades:{" "}
                        {Array.isArray(proposal.projectSkills)
                          ? proposal.projectSkills.join(", ")
                          : proposal.projectSkills || "No especificadas"}
                      </Typography>
                      <Typography variant="body2" sx={styles.timeToDo}>
                        Fecha de entrega:{" "}
                        {proposal.timeToDo
                          ? formatDate(proposal.timeToDo)
                          : "N/A"}
                      </Typography>
                      {userType === "cliente" && (
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={() =>
                            handleButtonClick(
                              proposal.projectId,
                              proposal.id,
                              proposal.uid
                            )
                          }
                        >
                          {clickedProposals[proposal.id]
                            ? "Ir a Chats"
                            : "Chatear"}
                        </Button>
                      )}
                    </Card>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No se encontraron propuestas.</Typography>
            )}
          </>
        )}
      </Container>
      <BottomNav />
    </Container>
  );
};

export default Proposals;
